import { Button } from '@askable/ui/core/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from '@askable/ui/core/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@askable/ui/core/select';
import { Check } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'urql';

import { Projects } from 'containers/Root/Projects/data/Projects.query';

const useResearcherProjects = () => {
  const [{ fetching, data }] = useQuery({
    query: Projects,
    requestPolicy: 'cache-and-network',
    variables: {
      filter: {
        type: { eq: 1 }, // Askable Plus projects
      },
    },
  });

  const projects = useMemo(() => {
    return data?.projects?.nodes ?? [];
  }, [data?.projects?.nodes]);

  return {
    projects,
    isLoading: fetching,
    isEmpty: !fetching && projects.length === 0,
  };
};

type AddToProjectDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onProjectSelect: (projectId: string) => void;
};

export const AddToProjectDialog = ({ isOpen, onOpenChange, onProjectSelect }: AddToProjectDialogProps) => {
  const { t } = useTranslation();
  const { projects, isLoading, isEmpty } = useResearcherProjects();
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');

  const selectedProject = projects.find(p => p?._id === selectedProjectId);

  const handleSubmit = () => {
    if (selectedProjectId) {
      onProjectSelect(selectedProjectId);
      setSelectedProjectId('');
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSelectedProjectId('');
    }
    onOpenChange(open);
  };

  const handleProjectSelect = (projectId: string) => {
    // Toggle selection if clicking the same project
    if (selectedProjectId === projectId) {
      setSelectedProjectId('');
    } else {
      setSelectedProjectId(projectId);
    }
  };

  const getPlaceholder = () => {
    if (isLoading) return t('global.loading');
    if (isEmpty) return t('sections.createStudy.addToProject.emptyState');
    return t('sections.createStudy.addToProject.selectPlaceholder');
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogTitle>{t('sections.createStudy.addToProject.add')}</DialogTitle>
        <DialogDescription>{t('sections.createStudy.addToProject.description')}</DialogDescription>
        <Select
          key={selectedProjectId || 'empty'}
          value=""
          onValueChange={handleProjectSelect}
          disabled={isLoading || isEmpty}
        >
          <SelectTrigger className="w-full">
            {selectedProject ? (
              <span className="truncate">{selectedProject.name ?? 'Untitled project'}</span>
            ) : (
              <span className="text-muted-foreground">{getPlaceholder()}</span>
            )}
          </SelectTrigger>
          <SelectContent className="w-[var(--radix-select-trigger-width)]">
            {projects.map(project =>
              project ? (
                <SelectItem key={project._id} value={project._id} className="pl-1">
                  <div className="flex items-center gap-2">
                    {selectedProjectId === project._id ? (
                      <Check className="h-4 w-4 shrink-0" />
                    ) : (
                      <div className="h-4 w-4 shrink-0" />
                    )}
                    <span className="truncate">{project.name ?? 'Untitled project'}</span>
                  </div>
                </SelectItem>
              ) : null,
            )}
          </SelectContent>
        </Select>
        <DialogFooter>
          <Button onClick={() => handleOpenChange(false)}>{t('global.cancel')}</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!selectedProjectId}>
            {t('sections.createStudy.addToProject.add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
